import React, { createContext, useState, useCallback, useContext, useRef, useEffect } from 'react'
import { AuthContext } from './AuthContext'
import apiService from '../services/ApiService'// Annotation Context for cross-component sync
/**
 * AnnotationContext - Provides shared annotation state across components
 * This enables real-time sync between MainPopup and full page views
 */
export const AnnotationContext = createContext(null)

export function AnnotationProvider({ children }) {
  const { isAuthenticated } = useContext(AuthContext)
  
  // Store annotations by documentId for efficient lookup
  // { documentId: { annotations: [], loading: false, error: null, lastFetched: timestamp } }
  const [annotationCache, setAnnotationCache] = useState({})
  
  // Track which documents are currently being fetched to avoid duplicate requests
  const fetchingRef = useRef(new Set())
  
  // Subscribers for annotation changes (for components that need to re-highlight)
  const subscribersRef = useRef(new Map()) // documentId -> Set of callbacks
  
  // Pending notifications queue (to notify after state updates)
  const pendingNotificationsRef = useRef([])

  /**
   * Subscribe to annotation changes for a document
   * Returns unsubscribe function
   * Immediately calls callback with cached data if available
   */
  const subscribe = useCallback((documentId, callback) => {
   // console.log('[AnnotationContext] Subscribing to:', documentId)
    if (!subscribersRef.current.has(documentId)) {
      subscribersRef.current.set(documentId, new Set())
    }
    subscribersRef.current.get(documentId).add(callback)
    
    // Immediately notify with cached data if available (prevents missing initial load)
    const cached = annotationCache[documentId]
    if (cached?.annotations && cached.annotations.length > 0) {
      // console.log('[AnnotationContext] Immediately notifying new subscriber with cached data:', cached.annotations.length, 'annotations')
      // Use setTimeout to ensure this happens after the component has finished mounting
      setTimeout(() => {
        try {
          callback([...cached.annotations])
        } catch (e) {
          console.error('[AnnotationContext] Error in immediate notification:', e)
        }
      }, 50)
    }
    
    return () => {
     // console.log('[AnnotationContext] Unsubscribing from:', documentId)
      const subs = subscribersRef.current.get(documentId)
      if (subs) {
        subs.delete(callback)
        if (subs.size === 0) {
          subscribersRef.current.delete(documentId)
        }
      }
    }
  }, [annotationCache])

  /**
   * Notify all subscribers of a document that annotations changed
   */
  const notifySubscribers = useCallback((documentId, annotations) => {
    const subs = subscribersRef.current.get(documentId)
   // console.log('[AnnotationContext] Notifying subscribers for:', documentId, 'count:', subs?.size || 0)
    if (subs && subs.size > 0) {
      subs.forEach(callback => {
        try {
          callback([...annotations]) // Pass a new array reference to ensure re-render
        } catch (e) {
          console.error('[AnnotationContext] Subscriber callback error:', e)
        }
      })
    }
  }, [])
  
  // Process pending notifications after state updates
  useEffect(() => {
    if (pendingNotificationsRef.current.length > 0) {
      const notifications = [...pendingNotificationsRef.current]
      pendingNotificationsRef.current = []
      
      // Process all pending notifications
      notifications.forEach(({ documentId, annotations }) => {
        notifySubscribers(documentId, annotations)
      })
    }
  }, [annotationCache, notifySubscribers])

  /**
   * Get annotations for a document from cache
   */
  const getAnnotations = useCallback((documentId) => {
    return annotationCache[documentId]?.annotations || []
  }, [annotationCache])

  /**
   * Check if annotations are loading for a document
   */
  const isLoading = useCallback((documentId) => {
    return annotationCache[documentId]?.loading || false
  }, [annotationCache])

  /**
   * Get error for a document
   */
  const getError = useCallback((documentId) => {
    return annotationCache[documentId]?.error || null
  }, [annotationCache])

  /**
   * Fetch annotations for a document from API
   * Uses cache if available and not stale
   */
  const fetchAnnotations = useCallback(async (documentId, forceRefresh = false) => {
    if (!documentId || !isAuthenticated) {
      return []
    }

    // Check if already fetching
    if (fetchingRef.current.has(documentId)) {
      // Wait for existing fetch to complete
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!fetchingRef.current.has(documentId)) {
            clearInterval(checkInterval)
            resolve(annotationCache[documentId]?.annotations || [])
          }
        }, 50)
      })
    }

    // Check cache (use cache if fetched within last 30 seconds and not forcing refresh)
    const cached = annotationCache[documentId]
    const cacheAge = cached?.lastFetched ? Date.now() - cached.lastFetched : Infinity
    
    if (!forceRefresh && cached?.annotations && cacheAge < 30000) {
      return cached.annotations
    }

    // Mark as fetching
    fetchingRef.current.add(documentId)
    
    // Set loading state
    setAnnotationCache(prev => ({
      ...prev,
      [documentId]: {
        ...prev[documentId],
        loading: true,
        error: null,
      }
    }))

    try {
      const response = await apiService.getAnnotationsForDocument(documentId)
      const annotations = response || []
      
      setAnnotationCache(prev => ({
        ...prev,
        [documentId]: {
          annotations,
          loading: false,
          error: null,
          lastFetched: Date.now(),
        }
      }))
      
      // Notify subscribers
      notifySubscribers(documentId, annotations)
      
      return annotations
    } catch (error) {
      console.error('[AnnotationContext] Failed to fetch annotations:', error)
      const errorMsg = 'Failed to load annotations. Please try again.'
      
      setAnnotationCache(prev => ({
        ...prev,
        [documentId]: {
          ...prev[documentId],
          loading: false,
          error: errorMsg,
        }
      }))
      
      return []
    } finally {
      fetchingRef.current.delete(documentId)
    }
  }, [isAuthenticated, annotationCache, notifySubscribers])

  /**
   * Add a new annotation to the cache (after successful POST)
   */
  const addAnnotation = useCallback((documentId, annotation) => {
   // console.log('[AnnotationContext] Adding annotation for:', documentId, annotation.id)
    
    setAnnotationCache(prev => {
      const existing = prev[documentId]?.annotations || []
      // Avoid duplicates
      if (existing.find(a => a.id === annotation.id)) {
       // console.log('[AnnotationContext] Annotation already exists, skipping')
        return prev
      }
      
      const newAnnotations = [...existing, annotation]
      
      // Queue notification for after state update
      pendingNotificationsRef.current.push({ documentId, annotations: newAnnotations })
      
      return {
        ...prev,
        [documentId]: {
          ...prev[documentId],
          annotations: newAnnotations,
          lastFetched: Date.now(),
        }
      }
    })
  }, [])

  /**
   * Update an annotation in the cache (after successful PUT)
   */
  const updateAnnotation = useCallback((documentId, annotationId, updatedData) => {
   // console.log('[AnnotationContext] Updating annotation:', documentId, annotationId)
    
    setAnnotationCache(prev => {
      const existing = prev[documentId]?.annotations || []
      const newAnnotations = existing.map(ann => 
        ann.id === annotationId ? { ...ann, ...updatedData } : ann
      )
      
      // Queue notification for after state update
      pendingNotificationsRef.current.push({ documentId, annotations: newAnnotations })
      
      return {
        ...prev,
        [documentId]: {
          ...prev[documentId],
          annotations: newAnnotations,
        }
      }
    })
  }, [])

  /**
   * Remove an annotation from the cache (after successful DELETE)
   */
  const removeAnnotation = useCallback((documentId, annotationId) => {
   // console.log('[AnnotationContext] Removing annotation:', documentId, annotationId)
    
    setAnnotationCache(prev => {
      const existing = prev[documentId]?.annotations || []
      const newAnnotations = existing.filter(ann => ann.id !== annotationId)
      
      // Queue notification for after state update
      pendingNotificationsRef.current.push({ documentId, annotations: newAnnotations })
      
      return {
        ...prev,
        [documentId]: {
          ...prev[documentId],
          annotations: newAnnotations,
        }
      }
    })
  }, [])

  /**
   * Clear cache for a specific document (useful when navigating away)
   */
  const clearCache = useCallback((documentId) => {
    if (documentId) {
      setAnnotationCache(prev => {
        const newCache = { ...prev }
        delete newCache[documentId]
        return newCache
      })
    } else {
      setAnnotationCache({})
    }
  }, [])

  /**
   * Invalidate cache for a document (force next fetch to refresh)
   */
  const invalidateCache = useCallback((documentId) => {
    setAnnotationCache(prev => ({
      ...prev,
      [documentId]: {
        ...prev[documentId],
        lastFetched: 0, // Force refresh on next fetch
      }
    }))
  }, [])

  const value = {
    // State accessors
    getAnnotations,
    isLoading,
    getError,
    
    // Data operations
    fetchAnnotations,
    addAnnotation,
    updateAnnotation,
    removeAnnotation,
    
    // Cache management
    clearCache,
    invalidateCache,
    
    // Subscriptions
    subscribe,
  }

  return (
    <AnnotationContext.Provider value={value}>
      {children}
    </AnnotationContext.Provider>
  )
}

/**
 * Custom hook to use AnnotationContext
 */
export function useAnnotationContext() {
  const context = useContext(AnnotationContext)
  if (!context) {
    throw new Error('useAnnotationContext must be used within an AnnotationProvider')
  }
  return context
}

export default AnnotationContext
